import { dayKey, fullDate } from './date-utils';

interface SprintLike {
  startDate: string;
  endDate: string;
}

/**
 * Selects the sprint that is currently active — the one whose [startDate, endDate]
 * window contains today.
 *
 * When today falls outside every sprint (a gap between sprints, or the past-sprints
 * query), it falls back to the most recently *started* sprint. The fallback deliberately
 * excludes sprints that have not started yet: the current-sprint query looks forward and
 * also returns the upcoming sprint, so an unfiltered "latest start" pick would select that
 * future sprint — the exact bug this helper exists to prevent. If no sprint has started,
 * it returns undefined rather than an upcoming one.
 *
 * Comparison happens on calendar-date strings (YYYY-MM-DD) via `fullDate`, the same
 * normalisation used everywhere else for sprint dates. Sprint dates are stored at
 * UTC-midnight; comparing them as instants against local-midnight bounds would drop the
 * active sprint on its first/last day for users in non-UTC timezones.
 */
export const getCurrentSprint = <T extends SprintLike>(sprints: T[] | undefined): T | undefined => {
  if (!sprints?.length) {
    return undefined;
  }

  const today = dayKey(new Date());

  const activeSprint = sprints.find(sprint => fullDate(sprint.startDate) <= today && fullDate(sprint.endDate) >= today);
  if (activeSprint) {
    return activeSprint;
  }

  // No sprint covers today: fall back to the most recently started sprint, never an upcoming one.
  return sprints
    .filter(sprint => fullDate(sprint.startDate) <= today)
    .sort((a, b) => (fullDate(a.startDate) < fullDate(b.startDate) ? 1 : -1))[0];
};

/**
 * Selects the sprint immediately before the given one — the most recently started sprint
 * whose start date precedes `current`'s.
 *
 * The members/products reporting lists paginate sprint-by-sprint and seed the first page
 * with a single sprint. The "past sprints" tab must start from the sprint *before* the
 * current one: the current sprint ends in the future, so seeding the list with it would
 * surface it as the first "past" sprint. Returns undefined when there is no earlier sprint.
 */
export const getPreviousSprint = <T extends SprintLike>(
  sprints: T[] | undefined,
  current: T | undefined,
): T | undefined => {
  if (!sprints?.length || !current) {
    return undefined;
  }

  return sprints
    .filter(sprint => fullDate(sprint.startDate) < fullDate(current.startDate))
    .sort((a, b) => (fullDate(a.startDate) < fullDate(b.startDate) ? 1 : -1))[0];
};

/** Newest first by start date, because sprint numbers restart each year and don't order across them. */
export const bySprintRecency = (a: SprintLike, b: SprintLike): number =>
  fullDate(b.startDate).localeCompare(fullDate(a.startDate));

/** Ended by `today` (YYYY-MM-DD), newest first; by date, as `getCurrentSprint` can fall back to an ended sprint. */
export const getEndedSprints = <T extends SprintLike>(sprints: T[], today: string): T[] =>
  sprints.filter(sprint => fullDate(sprint.endDate) < today).sort(bySprintRecency);

export interface SprintYearPage<T extends SprintLike> {
  /** The calendar year this page covers, as a label and as its identity. */
  year: number;
  /** The year's sprints, newest first. */
  sprints: T[];
}

/** The year a sprint belongs to, taken from the day it starts, so one running over New Year stays whole. */
export const sprintYear = (sprint: SprintLike): number => Number(fullDate(sprint.startDate).slice(0, 4));

/**
 * Sprint history cut into one page per calendar year, newest year first. Paging by a count of sprints
 * drifts until a page straddles New Year and shows two years side by side, which is exactly where sprint
 * numbers and dates are easiest to confuse.
 */
export const sprintYearPages = <T extends SprintLike>(sprints: T[]): SprintYearPage<T>[] => {
  const byYear = new Map<number, T[]>();
  sprints.forEach(sprint => {
    const year = sprintYear(sprint);
    const soFar = byYear.get(year);
    if (soFar) soFar.push(sprint);
    else byYear.set(year, [sprint]);
  });

  return [...byYear.entries()]
    .sort(([a], [b]) => b - a)
    .map(([year, yearSprints]) => ({
      year,
      sprints: yearSprints.slice().sort((a, b) => (fullDate(a.startDate) < fullDate(b.startDate) ? 1 : -1)),
    }));
};

/** The span a year's page covers: its first sprint's start to its last sprint's end. */
export const sprintPageRange = <T extends SprintLike>(sprints: T[]): { from: Date; to: Date } | undefined => {
  if (!sprints.length) return undefined;
  const starts = sprints.map(sprint => new Date(sprint.startDate).getTime());
  const ends = sprints.map(sprint => new Date(sprint.endDate).getTime());
  return { from: new Date(Math.min(...starts)), to: new Date(Math.max(...ends)) };
};
